'use client';
// ĐẾM MỌI LỆNH ĐỌC FIRESTORE PHÍA TRÌNH DUYỆT — lớp bọc thay cho `firebase/firestore`.
//
// VÌ SAO: đo ngày 14/09/2026 cho thấy **91% lượt đọc Firestore đến từ trình duyệt**, không phải server
// (server chỉ 9%: 69.776 / 777.308 doc). Nhưng bộ đếm client cũ chỉ được cắm tay ở **9 chỗ** trong khi
// repo có **244 lệnh đọc** (190 `getDocs` · 31 `getDoc` · 21 `onSnapshot` · 2 `getCountFromServer`)
// ⇒ giải thích được ~14%, còn lại mù. Cắm tay 244 chỗ là chắc chắn sót, và sót thì lại đoán mò.
//
// CÁCH LÀM: KHÔNG vá prototype như bên server được — SDK client xuất ra HÀM rời (`getDocs(q)`), không
// phải phương thức trên prototype. Nên thay bằng lớp bọc cùng tên, cùng kiểu, rồi đổi `import` ở 64 file.
// Bù lại có chốt chặn: `scripts/scan-client-reads.mjs` chặn mọi lệnh đọc nhập thẳng từ 'firebase/firestore'.
//
//     ❌ import { getDocs } from 'firebase/firestore';
//     ✅ import { getDocs } from '@/lib/fsx';
//
// ĐẾM ĐÚNG THEO HOÁ ĐƠN: Firestore tính **tối thiểu 1 lượt đọc cho mỗi truy vấn kể cả khi không khớp
// doc nào**. Bộ đếm cũ bỏ qua hoàn toàn truy vấn rỗng (`docs <= 0` thì return) nên luôn thấp hơn hoá đơn.
// Ở đây dùng `Math.max(1, size)`.
//
// TÊN NGUỒN lấy bằng API CÔNG KHAI (`doc.ref.parent.id`), KHÔNG dùng field nội bộ `_query` — SDK đổi
// tên field là mất sạch nhãn. Truy vấn rỗng thì không suy ra được tên ⇒ gộp vào `?empty`; vẫn ĐẾM đúng 1,
// chỉ mất phần tách theo nguồn.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════════
// ✅ ĐÃ GIẢI THÍCH VÀ VÁ (15/09/2026): trước đó bộ đếm này cao gấp ~2–5× hoá đơn
//
// Triệu chứng: 14/09 bộ đếm báo 2.786.083 doc phía client trong khi Cloud Monitoring báo TỔNG cả ngày
// (gồm server) chỉ 1.378.519.
//
// NGUYÊN NHÂN: `getDoc`/`getDocs` được phục vụ TỪ CACHE LOCAL vẫn bị đếm, dù không đi ra server nên
// KHÔNG bị tính tiền. Ca cụ thể: trang Chat gắn listener lên `conversations/{id}/readBy/{uid}` rồi
// badge lại `getDoc` đúng các đường dẫn đó → SDK trả thẳng từ cache.
//
// CÁCH TÌM RA (dùng lại được cho lần sau): so bộ đếm với Cloud Monitoring **theo TỪNG GIỜ** thay vì
// theo ngày, rồi đặt cạnh nhãn nghi ngờ. Tương quan hiện ra ngay — giờ `readBy` cao thì tỉ lệ 3–5×,
// giờ `readBy` = 0 thì tỉ lệ 0,4–0,8× (dưới 1 là đúng, vì phần còn lại là server). Gộp cả ngày thì
// hai thứ trộn vào nhau và không thấy gì.
//
// ĐÃ LOẠI TRỪ trước khi tìm ra, đừng kiểm lại: sự kiện analytics ghi hai lần (0/8.247 chữ ký lặp) ·
// lớp đặt nhãn kiêm việc cộng (`labelReads` chỉ đặt nhãn) · sai sót gộp của Cloud Monitoring (3 cách
// gộp cùng ra một số) · bất nhất nội bộ (tổng theo nhãn ≈ tổng `total`).
// ══════════════════════════════════════════════════════════════════════════════════════════════════
import {
  getDocs as _getDocs,
  getDoc as _getDoc,
  onSnapshot as _onSnapshot,
  getCountFromServer as _getCountFromServer,
  type DocumentData,
  type DocumentReference,
  type DocumentSnapshot,
  type QuerySnapshot,
} from 'firebase/firestore';
import { recordDocsRead } from './analytics';
import { logClientError } from './client-error-logger';

// ── NHÃN NGỮ NGHĨA ────────────────────────────────────────────────────────────────────────────────
// Tên collection trả lời "đọc BẢNG nào", nhưng thứ cần biết thường là "đọc VÌ VIỆC GÌ": 3 chỗ khác nhau
// cùng đọc `conversations` (trang danh sách · badge chưa đọc · provider todos) mà chi phí lệch hàng chục
// lần. Chính nhãn `todos-provider` đã chỉ ra thủ phạm 87,6% lượt đọc client ngày 13/09.
//
// Đặt nhãn ĐỒNG BỘ ngay trước lệnh đọc rồi xoá ngay sau khối đồng bộ đó — nên `Promise.all` nhiều lệnh
// trong cùng khối đều nhận đúng nhãn, mà nhãn không rơi sang lệnh đọc của nơi khác.
//
//     labelReads('todos-provider', () => Promise.all([getDocs(a), getDocs(b)]));
let pendingLabel: string | null = null;

export function labelReads<T>(label: string, run: () => T): T {
  pendingLabel = label;
  try { return run(); } finally { pendingLabel = null; }
}

/** Nhãn đang đặt (nếu có), nếu không thì tên collection suy từ kết quả. */
function label(fallback: () => string): string {
  return pendingLabel ?? fallback();
}

/**
 * Bọc đếm: hỏng thì BỎ QUA để không làm vỡ lệnh đọc thật, nhưng **báo MỘT lần**.
 *
 * Nuốt im hoàn toàn thì mọi số đo âm thầm về 0 và ta sẽ kết luận "trang này không đọc gì" — đúng dạng
 * nguy hiểm nhất: một kết quả trông hợp lệ nhưng sai. Chỉ báo 1 lần/phiên vì hỏng ở đây là hỏng ở MỌI
 * lệnh đọc, báo mỗi lần sẽ ngập Lỗi hệ thống và che mất lỗi thật.
 */
let meterBroken = false;

function safe(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    if (meterBroken) return;
    meterBroken = true;
    logClientError({
      route: 'lib/fsx', action: 'count_client_read',
      message: `Đếm lượt đọc phía client hỏng — SỐ ĐO TỪ ĐÂY KHÔNG ĐÁNG TIN: ${e instanceof Error ? e.message : String(e)}`,
    });
  }
}

function siteOfQuerySnap(snap: QuerySnapshot<DocumentData>): string {
  return snap.docs[0]?.ref.parent.id ?? '?empty';
}

function siteOfRef(ref: DocumentReference<DocumentData>): string {
  return ref.parent.id;
}

/**
 * Đọc MỘT LẦN phục vụ TỪ CACHE thì KHÔNG bị tính tiền — bỏ đếm.
 *
 * Đây là chỗ khác hẳn `onSnapshot` (xem ghi chú ở đó, nơi CỐ Ý không dùng `fromCache`): đọc một lần
 * không có "lần bắn thứ hai từ server" để bù, nên `fromCache === true` là kết luận cuối cùng —
 * không đi ra server, không tốn lượt đọc.
 *
 * Ca sinh ra nó trong GMS: trang Chat gắn listener lên `conversations/{id}/readBy/{uid}`, rồi badge
 * lại `getDoc` đúng những đường dẫn đó — SDK trả thẳng từ cache mà listener đã nạp sẵn.
 */
function billable(snap: unknown): boolean {
  const meta = (snap as { metadata?: { fromCache?: boolean } })?.metadata;
  return meta?.fromCache !== true;
}

export const getDocs = (async (q: Parameters<typeof _getDocs>[0]) => {
  const snap = await _getDocs(q as never);
  const lbl = label(() => siteOfQuerySnap(snap as QuerySnapshot<DocumentData>));
  safe(() => { if (billable(snap)) recordDocsRead(lbl, Math.max(1, snap.size)); });
  return snap;
}) as typeof _getDocs;

export const getDoc = (async (ref: Parameters<typeof _getDoc>[0]) => {
  const snap = await _getDoc(ref as never);
  const lbl = label(() => siteOfRef(ref as DocumentReference<DocumentData>));
  safe(() => { if (billable(snap)) recordDocsRead(lbl, 1); });
  return snap;
}) as typeof _getDoc;

/**
 * `getCountFromServer` KHÔNG đọc từng doc — Firestore tính ~1 lượt đọc cho mỗi 1000 doc được đếm.
 * Ghi 1 để biết chỗ nào đang dùng, đừng cộng `count` vào (sẽ thổi số lên hàng nghìn lần).
 */
export const getCountFromServer = (async (q: Parameters<typeof _getCountFromServer>[0]) => {
  const snap = await _getCountFromServer(q as never);
  const lbl = label(() => 'count()');
  safe(() => recordDocsRead(lbl, 1));
  return snap;
}) as typeof _getCountFromServer;

/**
 * `onSnapshot` KHÁC `getDocs`: lần đầu trả TOÀN BỘ kết quả, các lần sau chỉ trả phần THAY ĐỔI.
 * Cộng `snap.size` mọi lần là thổi số lên nhiều lần (một listener mở cả phiên sẽ "đọc" lại toàn bộ
 * mỗi khi có 1 doc đổi). Lần đầu đếm `size`, các lần sau đếm `docChanges().length`.
 *
 * ⚠️ KHÔNG đếm **tiếng vọng ghi tại chỗ** (`metadata.hasPendingWrites`) — sửa 15/09/2026: `updateDoc`
 * làm listener bắn ngay trước khi server xác nhận, rồi bắn lại lần nữa khi xác nhận xong ⇒ một lần GHI
 * bị đếm thành hai lượt ĐỌC, mà lần vọng đó không hề đi ra server.
 *
 * ⚠️ **ĐỔI CÁCH ĐẾM 23/09/2026 khi bật cache bền** (`persistentLocalCache`, xem `lib/firebase.ts`):
 * bản cũ CỐ Ý đếm cả lần bắn `fromCache` vì khi chưa có cache bền, lần bắn từ cache là thứ duy nhất
 * chắc chắn xảy ra. Có cache bền thì chính lần bắn đó KHÔNG đi ra server (SDK phục vụ từ IndexedDB
 * rồi mới đồng bộ bằng resume token) — đếm nó là **thổi số đúng cái phần vừa tiết kiệm được**, tức
 * bộ đếm sẽ không bao giờ cho thấy tối ưu có tác dụng.
 * Nay: bỏ qua lần bắn `fromCache`, và **luôn** đếm `docChanges().length`. Lần gắn NGUỘI (chưa có
 * cache) thì lần bắn đầu từ server có `docChanges` = toàn bộ kết quả ⇒ vẫn đếm đủ; lần gắn ẤM chỉ đếm
 * đúng phần server gửi về. Không còn cần cờ `first`.
 *
 * Nhận MỌI dạng overload bằng cách tìm đối số HÀM ĐẦU TIÊN (`onNext`) rồi bọc nó — không đoán vị trí,
 * nên dạng `onSnapshot(ref, options, onNext, onError)` cũng chạy đúng.
 */
export const onSnapshot = ((...args: unknown[]) => {
  const idx = args.findIndex((a, i) => i > 0 && typeof a === 'function');
  if (idx < 0) return (_onSnapshot as (...a: unknown[]) => () => void)(...args);   // dạng observer — trả nguyên, không đếm
  const onNext = args[idx] as (snap: unknown) => void;
  // Chốt nhãn tại lúc ĐĂNG KÝ: listener sống cả phiên, lúc nó bắn về thì `pendingLabel` đã bị xoá từ lâu.
  const fixed = pendingLabel;
  const wrapped = (snap: unknown) => {
    safe(() => {
      const meta = (snap as { metadata?: { fromCache?: boolean; hasPendingWrites?: boolean } })?.metadata;
      // Tiếng vọng của lệnh ghi chưa được server xác nhận → không đi ra server, không tốn lượt đọc.
      // Chỉ BỎ ĐẾM; `onNext` vẫn phải chạy đúng MỘT lần ở cuối `wrapped` — gọi `onNext` ngay tại đây
      // rồi `return` là làm trang nhận snapshot HAI lần.
      if (meta?.hasPendingWrites === true) return;
      // Phục vụ từ cache bền ⇒ không có request nào ra server ⇒ không tốn lượt đọc.
      if (meta?.fromCache === true) return;
      const qs = snap as QuerySnapshot<DocumentData>;
      if (typeof qs?.size === 'number') {
        const n = qs.docChanges().length;
        if (n > 0) recordDocsRead(fixed ?? siteOfQuerySnap(qs), n);
      } else {
        // Doc đơn: không có `docChanges`; mỗi lần bắn từ server (gắn lần đầu + mỗi lần doc đổi) là
        // đúng 1 lượt đọc.
        const ds = snap as DocumentSnapshot<DocumentData>;
        if (ds?.ref) recordDocsRead(fixed ?? siteOfRef(ds.ref), 1);
      }
    });
    onNext(snap);
  };
  const next = [...args];
  next[idx] = wrapped;
  return (_onSnapshot as (...a: unknown[]) => () => void)(...next);
}) as typeof _onSnapshot;
